import { useEffect, useRef } from "react";
import { teleproApi } from "../../lib/teleproApi";

/**
 * Phase 2 — Prospecting session lifecycle hook.
 *
 * On mount: POST /me/lists/{listId}/session/start (returns session doc +
 *           untreated count for the reprise banner).
 * Every 5 min: POST /me/lists/{listId}/session/heartbeat.
 * On unmount / page hide: POST /me/lists/{listId}/session/end.
 *
 * Silent fail — if the network is flaky, prospection UI keeps working.
 */
export function useProspectingSession(listId, onOpen) {
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (!listId) return undefined;
    let active = true;

    // Kick off session
    teleproApi.post(`/me/lists/${listId}/session/start`)
      .then((r) => { if (active) onOpen?.(r.data); })
      .catch(() => { /* silent */ });

    // Heartbeat every 5 min
    heartbeatRef.current = setInterval(() => {
      teleproApi.post(`/me/lists/${listId}/session/heartbeat`).catch(() => {});
    }, 5 * 60 * 1000);

    const endSession = () => {
      teleproApi.post(`/me/lists/${listId}/session/end`).catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") endSession();
    };
    window.addEventListener("pagehide", endSession);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      active = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      window.removeEventListener("pagehide", endSession);
      document.removeEventListener("visibilitychange", onVisibility);
      endSession();
    };
  // onOpen deliberately excluded — we want start called once per listId change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);
}
